import type { ReactNode } from 'react';
import { Badge, Modal } from '@/shared/ui';
import {
  COMPETITION_STATUS_LABELS,
  COMPETITION_STATUS_TONE,
  MATCH_TYPE_LABELS,
} from '../model/labels';
import {
  categoryLabel,
  COMPETITION_SYSTEM_LABELS,
  COMPETITION_TYPE_LABELS,
  DISCIPLINE_CODE_LABELS,
  formatDateRange,
  formatInstant,
} from '../model/tournamentList';
import type { ApiCompetition } from '../model/types';

/**
 * Podgląd konfiguracji rozgrywki.
 *
 * Wszystko, co pokazujemy, przyszło już z listą (`CompetitionResponse`), więc
 * otwarcie okna nie kosztuje kolejnego zapytania.
 */

const VISIBILITY_LABELS: Record<string, string> = {
  PUBLIC: 'Publiczna',
  PRIVATE: 'Prywatna',
};

const SCOPE_LABELS: Record<string, string> = {
  CITY: 'Miejska',
  NATIONAL: 'Ogólnopolska',
};

const SECOND_PHASE_LABELS: Record<string, string> = {
  PLAYOFF: 'Faza pucharowa',
  FINAL_GROUP: 'Grupa finałowa',
};

/** Wiersz „etykieta — wartość”. Puste wartości pokazujemy jako kreskę. */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="border-border flex items-baseline justify-between gap-4 border-b py-2 last:border-b-0">
      <dt className="text-fg-muted shrink-0 text-xs">{label}</dt>
      <dd className="text-fg text-right text-sm font-medium">{children || '—'}</dd>
    </div>
  );
}

/** Nagłówek sekcji w oknie podglądu. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-4 first:mt-0">
      <h3 className="text-fg-muted mb-1 text-[0.6875rem] font-semibold tracking-[0.06em] uppercase">
        {title}
      </h3>
      <dl>{children}</dl>
    </section>
  );
}

/** Opisuje format meczu — „Do 2 wygranych setów, super tie-break, bez przewag”. */
function scoringSummary(competition: ApiCompetition): string {
  const rules = competition.scoringRules;
  if (!rules) return '';

  const parts = [`Do ${Math.ceil(rules.bestOf / 2)} wygranych setów (best of ${rules.bestOf})`];
  if (rules.superTiebreak) parts.push('super tie-break');
  if (rules.noAd) parts.push('bez przewag');
  if (rules.shortSetTarget !== null) parts.push(`sety do ${rules.shortSetTarget}`);
  return parts.join(', ');
}

export interface TournamentDetailsModalProps {
  /** Rozgrywka do pokazania. `null` zamyka okno. */
  competition: ApiCompetition | null;
  onClose: () => void;
  /** Liczba zapisanych zawodników, jeśli znana. */
  participantCount?: number | undefined;
  /** Imię i nazwisko organizatora, jeśli udało się je pobrać. */
  ownerName?: string | undefined;
}

/** Okno z pełną konfiguracją rozgrywki — wywoływane ikoną podglądu w tabeli. */
export function TournamentDetailsModal({
  competition,
  onClose,
  participantCount,
  ownerName,
}: TournamentDetailsModalProps) {
  if (!competition) return null;

  const scoring = scoringSummary(competition);
  const points = competition.pointRules;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={competition.name}
      description={competition.description ?? undefined}
    >
      <Section title="Podstawowe">
        <Row label="Status">
          <Badge tone={COMPETITION_STATUS_TONE[competition.status]} size="sm">
            {COMPETITION_STATUS_LABELS[competition.status] ?? competition.status}
          </Badge>
        </Row>
        <Row label="Termin">{formatDateRange(competition.startDate, competition.endDate)}</Row>
        <Row label="Dyscyplina">
          {DISCIPLINE_CODE_LABELS[competition.discipline] ?? competition.discipline}
        </Row>
        <Row label="Rodzaj">{COMPETITION_TYPE_LABELS[competition.type] ?? competition.type}</Row>
        <Row label="Kategoria">{categoryLabel(competition)}</Row>
        <Row label="Typ meczu">
          {MATCH_TYPE_LABELS[competition.matchType] ?? competition.matchType}
        </Row>
        <Row label="Zawodnicy">
          {participantCount === undefined
            ? `limit ${competition.maxPlayers}`
            : `${participantCount} / ${competition.maxPlayers}`}
        </Row>
        <Row label="Wpisowe">
          {competition.entryFee
            ? `${competition.entryFee.amount} ${competition.entryFee.currency}`
            : 'Bez wpisowego'}
        </Row>
      </Section>

      <Section title="Format">
        <Row label="System">
          {COMPETITION_SYSTEM_LABELS[competition.system] ?? competition.system}
        </Row>
        {competition.playoffSize !== null && (
          <Row label="Wielkość drabinki">{competition.playoffSize}</Row>
        )}
        {competition.groupSize !== null && <Row label="Liczebność grupy">{competition.groupSize}</Row>}
        {competition.advancingPerGroup !== null && (
          <Row label="Awansuje z grupy">{competition.advancingPerGroup}</Row>
        )}
        {competition.secondPhaseType !== null && (
          <Row label="Druga faza">
            {SECOND_PHASE_LABELS[competition.secondPhaseType] ?? competition.secondPhaseType}
          </Row>
        )}
        <Row label="Mecz o 3. miejsce">{competition.thirdPlaceMatch ? 'Tak' : 'Nie'}</Row>
        {competition.bracketSize !== null && (
          <Row label="Drabinka po losowaniu">
            {competition.bracketSize} miejsc, {competition.totalRounds ?? '—'} rund
          </Row>
        )}
      </Section>

      <Section title="Zasady">
        <Row label="Format meczu">{scoring}</Row>
        {points && (
          <Row label="Punktacja">
            zwycięstwo {points.winPoints}, porażka {points.lossPoints}, walkower{' '}
            {points.walkoverWinPoints}/{points.walkoverLossPoints}
          </Row>
        )}
        <Row label="Awanse / spadki">
          {competition.promotions || competition.relegations
            ? [competition.promotions ? 'awanse' : null, competition.relegations ? 'spadki' : null]
                .filter(Boolean)
                .join(', ')
            : 'Brak'}
        </Row>
      </Section>

      <Section title="Metryka">
        <Row label="Widoczność">
          {VISIBILITY_LABELS[competition.visibility] ?? competition.visibility}
        </Row>
        <Row label="Zasięg">
          {[SCOPE_LABELS[competition.scope] ?? competition.scope, competition.city]
            .filter(Boolean)
            .join(' • ')}
        </Row>
        <Row label="Utworzono">{formatInstant(competition.createdAt)}</Row>
        <Row label="Ostatnia zmiana">{formatInstant(competition.updatedAt)}</Row>
        <Row label="Organizator">{ownerName}</Row>
        <Row label="Identyfikator">
          <code className="text-fg-subtle text-xs">{competition.id}</code>
        </Row>
      </Section>
    </Modal>
  );
}
